import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { usePlayers, useStaff } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { cn, initials, POSITION_LABELS, POSITION_ORDER } from '@/lib/utils'
import type { Position } from '@/lib/types'
import { Container, SectionHeading } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { PlayerCard } from '@/components/site/cards'
import { EmptyState, LoadingBlock, ErrorState } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

type Filter = 'all' | Position

export default function SquadPage() {
  usePageMeta('Squad', 'Every player in the AFC Vikings Cairo first team, by position.')

  const { data: players = [], isLoading, isError } = usePlayers()
  const { data: staff = [] } = useStaff()
  const [filter, setFilter] = useState<Filter>('all')

  const grouped = useMemo(() => {
    const visible = filter === 'all' ? players : players.filter((p) => p.position === filter)
    return POSITION_ORDER.map((position) => ({
      position,
      players: visible.filter((player) => player.position === position),
    })).filter((group) => group.players.length > 0)
  }, [players, filter])

  const tabs: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: players.length },
    ...POSITION_ORDER.map((position) => ({
      key: position as Filter,
      label: `${POSITION_LABELS[position]}s`,
      count: players.filter((player) => player.position === position).length,
    })).filter((tab) => tab.count > 0),
  ]

  return (
    <>
      <PageHero
        kicker="The squad"
        rail={`${players.length} players`}
        title="Squad"
        subtitle="Everyone who pulls on the shirt. Numbers, positions and the record they have built."
      />

      <section className="border-hairline bg-ink border-b py-12 md:py-20">
        <Container>
          {isError ? (
            <ErrorState title="The squad did not load" />
          ) : isLoading ? (
            <LoadingBlock label="Loading the squad" />
          ) : players.length === 0 ? (
            <EmptyState
              title="No players yet"
              description="Players added in the club dashboard appear here straight away."
            />
          ) : (
            <>
              <div
                className="no-scrollbar border-hairline flex gap-1 overflow-x-auto border-b"
                role="tablist"
                aria-label="Filter by position"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={filter === tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={cn(
                      'condensed relative shrink-0 px-4 py-3.5 text-[0.8125rem] font-semibold tracking-[0.16em] transition-colors',
                      filter === tab.key ? 'text-blood' : 'text-bone-faint hover:text-bone-dim'
                    )}
                  >
                    {tab.label}
                    <span className="ml-2 text-[0.6875rem] opacity-60">{tab.count}</span>
                    {filter === tab.key ? (
                      <motion.span
                        layoutId="squad-page-tab"
                        className="bg-blood absolute inset-x-0 -bottom-px h-0.5"
                      />
                    ) : null}
                  </button>
                ))}
              </div>

              <div className="mt-12 space-y-16">
                {grouped.map((group) => (
                  <div key={group.position}>
                    <div className="mb-6 flex items-baseline gap-4">
                      <h2 className="condensed text-bone text-xl font-bold tracking-[0.1em]">
                        {POSITION_LABELS[group.position]}s
                      </h2>
                      <span className="bg-hairline h-px flex-1" aria-hidden />
                      <span className="condensed text-bone-faint text-[0.6875rem] tracking-[0.2em]">
                        {String(group.players.length).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                      {group.players.map((player, index) => (
                        <PlayerCard key={player.id} player={player} index={index} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Container>
      </section>

      {staff.length > 0 ? (
        <section className="bg-ink-sunken py-16 md:py-24">
          <Container>
            <SectionHeading eyebrow="Behind the team" title="Coaching & club staff" />
            <div className="border-hairline bg-hairline mt-10 grid gap-px border sm:grid-cols-2 lg:grid-cols-4">
              {staff.map((member) => {
                const photo = fileUrl(member, member.photo, '400x400')
                return (
                  <div key={member.id} className="bg-ink p-6">
                    {photo ? (
                      <img
                        src={photo}
                        alt={member.name}
                        loading="lazy"
                        className="mb-5 aspect-square w-20 object-cover"
                      />
                    ) : (
                      <div className="condensed border-hairline text-bone-faint mb-5 flex aspect-square w-20 items-center justify-center border text-lg">
                        {initials(member.name)}
                      </div>
                    )}
                    <p className="condensed text-bone text-lg font-bold tracking-[0.04em]">
                      {member.name}
                    </p>
                    <p className="eyebrow text-blood mt-1.5">{member.role}</p>
                    {member.bio ? (
                      <div
                        className="prose-viking mt-3 text-[0.875rem]"
                        dangerouslySetInnerHTML={{ __html: member.bio }}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          </Container>
        </section>
      ) : null}
    </>
  )
}
